// Desktop-oriented site header — logo, nav (Catalog / Send a request /
// Track a request / Contact), sign-in link, language toggle. Used by
// pages/LandingPage.jsx only for now (Direction B desktop redesign, entry
// 41) — every other portal screen keeps components/PortalShell.jsx's slim
// bar unchanged, per this task's own "mobile stays roughly as-is" scope.
//
// The nav row itself hides below CatalogBrowsePage.module.css's shared
// breakpoint (see SiteHeader.module.css) — logo, sign-in, and the language
// toggle stay visible at every width; a 360px screen doesn't get a
// hamburger menu (not asked for), it just relies on the hero's own actions
// and the "Sign in" link instead.
//
// "Contact" is an in-page anchor (#contact, components/SiteFooter.jsx),
// not a route — this header is only ever rendered on the same page as that
// footer.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useCompanyInfo } from '../hooks/useCompanyInfo.js';
import { peekPendingLineCount, PENDING_LINE_CHANGED_EVENT } from '../utils/pendingLine.js';
import LanguageToggle from './LanguageToggle.jsx';
import styles from './SiteHeader.module.css';

export default function SiteHeader() {
  const { t } = useLanguage();

  // Entry Point 2 (catalog-picker task): a count badge, not a persistent
  // bar — on a phone, a bar is expensive in screen space, and this header
  // (unlike the nav row beside it) already stays visible at every width.
  // A plain `window` event, not a store/context: this app has no other
  // cross-component reactive state and one more dependency-free listener
  // is cheaper than pulling in anything for it.
  const [pendingCount, setPendingCount] = useState(() => peekPendingLineCount());
  useEffect(() => {
    const update = () => setPendingCount(peekPendingLineCount());
    window.addEventListener(PENDING_LINE_CHANGED_EVENT, update);
    return () => window.removeEventListener(PENDING_LINE_CHANGED_EVENT, update);
  }, []);
  // The brand mark is the company name from settings; app.brand ("SineEthel")
  // is only the fallback until an admin sets one, so the logo is never blank.
  const company = useCompanyInfo();
  // Customer-accounts task: a logged-in customer sees "My account" instead
  // of "Sign in" — same conditional-link shape this header already had for
  // sign-in, just branching on session state now. Not a redesign (no new
  // colors/layout), so it stays within this task's "don't touch the
  // palette" constraint.
  const { user, logout } = useAuth();
  const isCustomer = Boolean(user) && user.role === 'customer';
  const navigate = useNavigate();

  // Log-out task: a logged-in customer gets a small disclosure (native
  // <details>/<summary>, no new dependency) instead of a plain link, so
  // "Log out" has somewhere to live. A guest still just gets the plain
  // "Sign in" link below — unchanged.
  async function handleLogout(event) {
    event.preventDefault();
    // Close the <details> disclosure before the auth state flips, otherwise
    // the now-guest header re-renders with the menu still (uselessly) open.
    const details = event.currentTarget.closest('details');
    if (details) details.open = false;
    await logout();
    navigate('/');
  }

  return (
    <header className={styles.header}>
      <Link to="/" className={styles.brand}>
        {company.name || t('app.brand')}
      </Link>

      <nav className={styles.nav}>
        <Link to="/browse" className={styles.navLink}>
          {t('portal.nav.catalog')}
        </Link>
        {/* A logged-in customer goes straight to the account form
            (POST /requests, no OTP — their session already proves the
            email). A guest gets /start, the email-OTP-verify flow. */}
        <Link to={isCustomer ? '/request' : '/start'} className={styles.navLink}>
          {t('portal.nav.sendRequest')}
        </Link>
        {/* Same /track target for everyone — but a logged-in customer lands
            on their OWN request list there (no ref number needed), so the
            label says so. "My account" is settings only now. */}
        <Link to="/track" className={styles.navLink}>
          {isCustomer ? t('portal.nav.myRequests') : t('portal.nav.trackRequest')}
        </Link>
        <a href="#contact" className={styles.navLink}>
          {t('portal.nav.contact')}
        </a>
      </nav>

      <div className={styles.actions}>
        {pendingCount > 0 && (
          <Link to={isCustomer ? '/request' : '/start'} className={styles.signInLink}>
            {t('portal.nav.pendingCount', { count: pendingCount })}
          </Link>
        )}
        {isCustomer ? (
          <details className={styles.accountMenu}>
            <summary className={styles.signInLink}>{t('portal.nav.myAccount')}</summary>
            <div className={styles.accountMenuPanel}>
              <Link to="/account" className={styles.accountMenuLink}>
                {t('portal.nav.myAccount')}
              </Link>
              <button type="button" className={styles.accountMenuButton} onClick={handleLogout}>
                {t('nav.logout')}
              </button>
            </div>
          </details>
        ) : (
          <Link to="/login" className={styles.signInLink}>
            {t('portal.login.title')}
          </Link>
        )}
        <LanguageToggle />
      </div>
    </header>
  );
}
